import { faArrowDown19, faArrowDown91, faPlay, faCheck } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSerieByUrl } from '../../api';
import ImageLoader from '../../common/ImageLoader';
import ResponsiveContainer from '../../common/ResponsiveContainer';
import useSettingsStore from '../../hooks/useSettingsStore';

const sortCapitulos = (capitulos, ascendiente) =>
  [...capitulos].sort((a, b) => (ascendiente ? a.numero - b.numero : b.numero - a.numero));

const MISSING_THUMBNAIL = 'missing-thumbnail.png';

const getThumbnail = (cap, fallback) => {
  if (cap.thumbnail && !cap.thumbnail.includes(MISSING_THUMBNAIL)) return cap.thumbnail;
  if (cap.url) return cap.url.replace('turkish4love.com/videos/', 'turkish4love.com/thumbnails/').replace('.mp4', '.jpeg');
  return fallback || null;
};

const Capitulo = ({ capitulo, fallbackImage }) => {
  const watched = !!capitulo?.completedAt;
  const hasProgress = !watched && capitulo?.time > 5 && capitulo?.duration > 0;
  const progress = hasProgress ? Math.min((capitulo.time / capitulo.duration) * 100, 100) : 0;

  return (
    <Link
      to={String(capitulo?.numero)}
      className='group relative flex flex-col rounded-lg bg-white shadow overflow-hidden border border-gray-100 hover:border-red-1 transition-colors'
    >
      <div className='relative w-full aspect-video bg-gray-200 overflow-hidden'>
        <img
          src={getThumbnail(capitulo, fallbackImage)}
          alt={`Episodio ${capitulo?.numero}`}
          className='w-full h-full object-cover'
          loading='lazy'
          onError={fallbackImage ? (e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; } : undefined}
        />
        <div className='absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors'>
          <FontAwesomeIcon icon={faPlay} className='text-white text-xl drop-shadow' />
        </div>
        {watched && (
          <div className='absolute inset-0 bg-black/50 flex items-center justify-center'>
            <FontAwesomeIcon icon={faCheck} className='text-white text-2xl drop-shadow' />
          </div>
        )}
        {hasProgress && (
          <div className='absolute bottom-0 left-0 right-0 h-1 bg-black/30'>
            <div className='h-full bg-red-1' style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <div className='px-3 py-2'>
        <p className='text-xs font-semibold font-montserrat uppercase tracking-wide text-gray-400'>EP {capitulo?.numero}</p>
        <p className='text-sm text-gray-800 font-montserrat truncate'>Episodio {capitulo?.numero}</p>
        {watched && (
          <span className='inline-block mt-1 bg-green-100 text-green-700 text-[10px] font-semibold font-montserrat px-1.5 py-0.5 rounded-full leading-none'>
            Visto
          </span>
        )}
      </div>
    </Link>
  );
};

const Serie = () => {
  const [serieData, setSerieData] = useState(null);
  const ascendiente = useSettingsStore((state) => state.episodeSortAsc);
  const toggleEpisodeSort = useSettingsStore((state) => state.toggleEpisodeSort);
  const [soloVistos, setSoloVistos] = useState(false);

  const { serie } = useParams();
  useEffect(() => {
    getSerieByUrl(serie).then((res) => {
      setSerieData(res.data);
    });
  }, []);

  useEffect(() => {
    if (serieData) {
      const capitulos = serieData.capitulos;
      if (soloVistos) {
        setSerieData({ ...serieData, capitulos: capitulos.filter((c) => !!c.completedAt) });
      } else {
        setSerieData({ ...serieData, capitulos });
      }
    }
  }, [ascendiente, soloVistos]);

  if (!serieData) {
    return (
      <div>
        <div className='w-full h-56 md:h-80 bg-gray-200 animate-pulse' />
        <ResponsiveContainer className='relative -mt-16 md:-mt-24 pb-10'>
          <div className='flex items-end gap-5 mb-6'>
            <div className='w-24 md:w-36 aspect-[4/6] rounded-xl bg-gray-300 animate-pulse flex-shrink-0 shadow-xl' />
            <div className='pb-1 flex flex-col gap-2'>
              <div className='h-7 w-48 bg-gray-200 animate-pulse rounded' />
              <div className='flex gap-2'>
                <div className='h-5 w-16 bg-gray-200 animate-pulse rounded-full' />
                <div className='h-5 w-16 bg-gray-200 animate-pulse rounded-full' />
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-2 mb-8'>
            <div className='h-4 w-full bg-gray-100 animate-pulse rounded' />
            <div className='h-4 w-4/5 bg-gray-100 animate-pulse rounded' />
            <div className='h-4 w-2/3 bg-gray-100 animate-pulse rounded' />
          </div>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
            {[...Array(8)].map((_, i) => (
              <div key={i} className='rounded-lg overflow-hidden bg-gray-100 animate-pulse aspect-video' />
            ))}
          </div>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div>
      {/* Cinematic backdrop */}
      <div className='relative w-full h-56 md:h-[380px] overflow-hidden'>
        <img
          src={serieData.backdrop}
          alt=''
          className='w-full h-full object-cover object-top'
        />
        {/* Top vignette */}
        <div className='absolute inset-0 bg-gradient-to-b from-black/30 to-transparent' />
        {/* Bottom fade to page bg */}
        <div className='absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent' />
        {/* Left vignette */}
        <div className='absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-transparent' />
      </div>

      {/* Content */}
      <ResponsiveContainer className='relative -mt-16 md:-mt-24 pb-10'>
        {/* Poster + title */}
        <div className='flex items-end gap-4 md:gap-6 mb-5'>
          <ImageLoader
            src={serieData.imagen_catalogo}
            className='w-24 md:w-36 aspect-[4/6] rounded-xl shadow-2xl flex-shrink-0 border-2 border-white overflow-hidden'
          />
          <div className='pb-1 min-w-0'>
            <h1 className='text-xl md:text-4xl font-bold font-montserrat text-gray-900 leading-tight mb-2 truncate'>
              {serieData.titulo_original}
            </h1>
            <div className='flex flex-wrap gap-1.5'>
              {serieData.estado && (
                <span className={`text-xs font-semibold font-montserrat px-2.5 py-1 rounded-full ${
                  serieData.estado.includes('emisión') ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {serieData.estado}
                </span>
              )}
              {serieData.generos?.map((genero, i) => (
                <span key={i} className='bg-red-1 text-white text-xs font-semibold font-montserrat px-2.5 py-1 rounded-full'>
                  {genero}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Description */}
        <div
          className='prose prose-sm md:prose-base prose-gray max-w-none font-montserrat mb-8 line-clamp-5 md:line-clamp-none'
          dangerouslySetInnerHTML={{ __html: serieData.descripcion }}
        />

        {/* Episodes header + sort */}
        <div className='flex items-center gap-3 mb-4'>
          <span className='font-bold font-montserrat text-gray-800'>Episodios</span>
          <span className='text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-montserrat'>
            {serieData.capitulos.length} eps
          </span>
          <button
            onClick={toggleEpisodeSort}
            className='ml-auto flex items-center gap-2 text-sm font-semibold font-montserrat text-gray-500 hover:text-red-1 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors'
          >
            <FontAwesomeIcon icon={ascendiente ? faArrowDown19 : faArrowDown91} />
            {ascendiente ? 'Ascendente' : 'Descendente'}
          </button>
        </div>

        {/* Episode grid */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          {sortCapitulos(serieData.capitulos, ascendiente).map((capitulo, index) => (
            <Capitulo key={index} capitulo={capitulo} fallbackImage={serieData.imagen_catalogo} />
          ))}
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default Serie;
